/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { AnyAction, createSlice } from '@reduxjs/toolkit';

import { ApiStatus } from '../../../ts';
import type { DiscoverySlice } from '../../../ts/state';
import {
  CONFIDENCE_THRESHOLD_NUMBER,
  CONFIDENCE_THRESHOLD_NUMBER_DISABLED,
  CONFIDENCE_THRESHOLD_VALUE,
  NUMBER_OF_RESULTS,
  RESPONSE_LENGTH_VALUE
} from '../util/constants';
import { getDefaultFieldValueForV2Projects } from '../util/search-skill';
import {
  listInstances,
  listProjects,
  listFields,
  listFieldExamples
} from './actions';
import { discoveryAdapter } from './adapter';

const initialState: DiscoverySlice = discoveryAdapter.getInitialState({
  listInstanceStatus: ApiStatus.IDLE,
  listInstanceFetched: null,
  listInstanceError: null,
  listProjectStatus: ApiStatus.IDLE,
  listProjectError: null,
  unavailableInstances: [],

  selectedInstanceId: null,
  selectedProjectId: null,
  draft: null,
  isDirty: false,
  isChangingProject: false,
  isForceInstanceList: false,
  showDetailConfig: false,
  skillId: null,

  listFieldsStatus: ApiStatus.IDLE,
  listFieldsError: null,
  listFieldExamplesStatus: ApiStatus.IDLE,
  listFieldExamplesError: null,
  fields: [],
  fieldExamples: []
});

// Slice & Reducer
const slice = createSlice({
  name: 'discovery',
  initialState,
  reducers: {
    selectInstanceId(state: DiscoverySlice, action) {
      state.selectedInstanceId = action.payload;
      state.selectedProjectId = null;
      state.isDirty = true;
    },
    selectProjectId(state: DiscoverySlice, action) {
      state.selectedProjectId = action.payload;
      state.isDirty = true;
    },
    setSearchSkillDraft(state: DiscoverySlice, action) {
      const { skill, isDirty } = action.payload;
      state.draft = skill || null;
      state.isDirty = isDirty;
    },
    clearElasticDetails(state: DiscoverySlice) {
      delete state?.draft?.search_settings?.elastic_search;
    },
    setShowDetailConfig(state: DiscoverySlice, action) {
      state.showDetailConfig = action.payload;
    },
    setIsChangingProject(state: DiscoverySlice, action) {
      state.isChangingProject = action.payload;
    },
    setIsForceInstanceList(state: DiscoverySlice, action: AnyAction) {
      state.isForceInstanceList = action.payload;
    },
    updateDraftInstanceProjectIds(state: DiscoverySlice, action: AnyAction) {
      const { language, url } = action.payload;
      state.draft.search_settings.discovery.instance_id =
        state.selectedInstanceId;
      state.draft.search_settings.discovery.project_id =
        state.selectedProjectId;
      state.draft.language = language;
      state.draft.search_settings.discovery.url = url;
      // reset the schema fields, which vary per project/collection
      state.draft.search_settings.schema_mapping = {
        title: '',
        body: '',
        url: ''
      };
      // intercepted by ui-server to know to update creds
      state.draft.instance_change = true;
      state.isDirty = true;
    },
    setDraftMaxPrimaryResults(state: DiscoverySlice, action: AnyAction) {
      if (state.draft?.search_settings?.discovery) {
        state.draft.search_settings.discovery.max_primary_results =
          action.payload;
      }
      // also update the max_total_results if !isDisplayShowMore (if < 10)
      if (
        state.draft.search_settings.discovery.max_total_results <
        NUMBER_OF_RESULTS.SHOW_MORE_MAX
      ) {
        state.draft.search_settings.discovery.max_total_results =
          action.payload;
      }
      state.isDirty = true;
    },
    setDraftIsRefinedResults(state: DiscoverySlice, action: AnyAction) {
      if (!!action.payload) {
        state.draft.search_settings.discovery.confidence_threshold =
          CONFIDENCE_THRESHOLD_NUMBER;
      } else {
        state.draft.search_settings.discovery.confidence_threshold =
          CONFIDENCE_THRESHOLD_NUMBER_DISABLED;
      }
      state.isDirty = true;
    },
    setDraftIsHighlightAnswers(state: DiscoverySlice, action: AnyAction) {
      state.draft.search_settings.discovery.find_answers = !!action.payload;
      state.isDirty = true;
    },
    setDraftIsDisplayShowMore(state: DiscoverySlice, action: AnyAction) {
      if (!!action.payload) {
        state.draft.search_settings.discovery.max_total_results =
          NUMBER_OF_RESULTS.SHOW_MORE_MAX;
      } else {
        state.draft.search_settings.discovery.max_total_results =
          state.draft.search_settings.discovery.max_primary_results;
      }
      state.isDirty = true;
    },
    setDraftSuccessMessage(state: DiscoverySlice, action: AnyAction) {
      state.draft.search_settings.messages.success = action.payload;
      state.isDirty = true;
    },
    setDraftNoResultsMessage(state: DiscoverySlice, action: AnyAction) {
      state.draft.search_settings.messages.no_result = action.payload;
      state.isDirty = true;
    },
    setDraftConnectivityMessage(state: DiscoverySlice, action: AnyAction) {
      state.draft.search_settings.messages.error = action.payload;
      state.isDirty = true;
    },
    setDraftTitleOptionValue(state: DiscoverySlice, action: AnyAction) {
      state.draft.search_settings.schema_mapping.title = action.payload;
      state.isDirty = true;
    },
    setDraftBodyOptionValue(state: DiscoverySlice, action: AnyAction) {
      state.draft.search_settings.schema_mapping.body = action.payload;
      state.isDirty = true;
    },
    setDraftUrlOptionValue(state: DiscoverySlice, action: AnyAction) {
      state.draft.search_settings.schema_mapping.url = action.payload;
      state.isDirty = true;
    },
    setDraftConversationalSearchEnabledDefault(state: DiscoverySlice) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        enabled: false
      };
    },
    setDraftConversationalSearchRetrievalConfidenceThresholdDefault(
      state: DiscoverySlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        pre_gen_threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftConversationalSearchConfidenceThresholdDefault(
      state: DiscoverySlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftConversationalSearchVerbosityOptionDefault(state: DiscoverySlice) {
      state.draft.search_settings.conversational_search.response_length = {
        ...state.draft.search_settings.conversational_search.response_length,
        option: RESPONSE_LENGTH_VALUE.MODERATE
      };
    },
    setDraftConversationalSearchResponseConfidenceThresholdDefault(
      state: DiscoverySlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        post_gen_threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftCitationsTitleValueDefault(state: DiscoverySlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        citations_title: action.payload
      };
    },
    setDraftMaxCitationsValueDefault(state: DiscoverySlice, action) {
      state.draft.search_settings.conversational_search.citations = {
        ...state.draft.search_settings.conversational_search.citations,
        max_citations: action.payload
      };
    },
    setDraftIsConversationalSearchEnabled(
      state: DiscoverySlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        enabled: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchMultiTurn(state: DiscoverySlice, action) {
      state.draft.search_settings.conversational_search.multi_turn = {
        ...state.draft.search_settings.conversational_search.multi_turn,
        enabled: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchMultiTurnDefault(state: DiscoverySlice) {
      state.draft.search_settings.conversational_search.multi_turn = {
        ...state.draft.search_settings.conversational_search.multi_turn,
        enabled: false
      };
    },
    setDraftConversationalSearchRetrievalConfidenceThreshold(
      state: DiscoverySlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        pre_gen_threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchConfidenceThreshold(
      state: DiscoverySlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchVerbosityOption(
      state: DiscoverySlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search.response_length = {
        ...state.draft.search_settings.conversational_search.response_length,
        option: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchResponseConfidenceThreshold(
      state: DiscoverySlice,
      action: AnyAction
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        post_gen_threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftCitationsTitleValue(state: DiscoverySlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        citations_title: action.payload
      };
      state.isDirty = true;
    },
    setDraftMaxCitationsValue(state: DiscoverySlice, action) {
      state.draft.search_settings.conversational_search.citations = {
        ...state.draft.search_settings.conversational_search.citations,
        max_citations: action.payload
      };
      state.isDirty = true;
    },
    clearInstanceSettings(state, _action) {
      state.fields = [];
      state.fieldExamples = [];
      state.draft.search_settings.schema_mapping = {
        title: '',
        body: '',
        url: ''
      };
      state.showDetailConfig = false;
    },
    setDraftDiscoveryFilterValue(state: DiscoverySlice, action) {
      state.draft.search_settings.discovery.filter = action.payload;
      state.isDirty = true;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(listInstances.pending, (state: DiscoverySlice) => {
        state.listInstanceStatus = ApiStatus.LOADING;
        state.listInstanceError = null;
      })
      .addCase(
        listInstances.fulfilled,
        (state: DiscoverySlice, action: AnyAction) => {
          state.listInstanceStatus = ApiStatus.SUCCEEDED;
          state.listInstanceFetched = Date.now();
          state.listInstanceError = null;
          state.unavailableInstances = action.payload.unavailableInstances;
          discoveryAdapter.setAll(state, action.payload.instances);
        }
      )
      .addCase(
        listInstances.rejected,
        (state: DiscoverySlice, action: AnyAction) => {
          state.listInstanceStatus = ApiStatus.ERROR;
          state.listInstanceError = action.error;
        }
      )
      .addCase(listProjects.pending, (state: DiscoverySlice) => {
        state.listProjectStatus = ApiStatus.LOADING;
        state.listProjectError = null;
      })
      .addCase(
        listProjects.fulfilled,
        (state: DiscoverySlice, action: AnyAction) => {
          state.listProjectStatus = ApiStatus.SUCCEEDED;
          state.listProjectError = null;
          discoveryAdapter.upsertOne(state, {
            ...action.payload,
            listProjectFetched: Date.now()
          });
        }
      )
      .addCase(
        listProjects.rejected,
        (state: DiscoverySlice, action: AnyAction) => {
          state.listProjectStatus = ApiStatus.ERROR;
          state.listProjectError = action.error;
        }
      )
      .addCase(listFields.pending, (state: DiscoverySlice) => {
        state.listFieldsStatus = ApiStatus.LOADING;
        state.listFieldsError = null;
        state.fields = [];
      })
      .addCase(listFields.fulfilled, (state: DiscoverySlice, action) => {
        state.listFieldsStatus = ApiStatus.SUCCEEDED;
        state.listFieldsError = null;
        const fields = action.payload.fields;
        state.fields = fields;
        const title = state.draft?.search_settings?.schema_mapping?.title;
        const body = state.draft?.search_settings?.schema_mapping?.body;
        const url = state.draft?.search_settings?.schema_mapping?.url;

        if (state.draft && !title && !body && !url) {
          const {
            defaultTitle = '',
            defaultBody = '',
            defaultUrl = ''
          } = getDefaultFieldValueForV2Projects(fields);
          state.draft.search_settings.schema_mapping.title = defaultTitle;
          state.draft.search_settings.schema_mapping.body = defaultBody;
          state.draft.search_settings.schema_mapping.url = defaultUrl;
        }
      })
      .addCase(
        listFields.rejected,
        (state: DiscoverySlice, action: AnyAction) => {
          state.listFieldsStatus = ApiStatus.ERROR;
          state.listFieldsError = action.error;
        }
      )
      .addCase(listFieldExamples.pending, (state: DiscoverySlice) => {
        state.listFieldExamplesStatus = ApiStatus.LOADING;
        state.listFieldExamplesError = null;
        state.fieldExamples = [];
      })
      .addCase(
        listFieldExamples.fulfilled,
        (state: DiscoverySlice, action: AnyAction) => {
          state.listFieldExamplesStatus = ApiStatus.SUCCEEDED;
          state.listFieldExamplesError = null;
          state.fieldExamples = action.payload.fieldExamples;
        }
      )
      .addCase(
        listFieldExamples.rejected,
        (state: DiscoverySlice, action: AnyAction) => {
          state.listFieldExamplesStatus = ApiStatus.ERROR;
          state.listFieldExamplesError = action.error;
        }
      );
  }
});

export const {
  selectInstanceId,
  selectProjectId,
  setSearchSkillDraft,
  clearElasticDetails,
  setIsChangingProject,
  setIsForceInstanceList,
  setShowDetailConfig,
  setDraftMaxPrimaryResults,
  setDraftIsRefinedResults,
  setDraftIsHighlightAnswers,
  setDraftIsDisplayShowMore,
  setDraftConversationalSearchEnabledDefault,
  setDraftConversationalSearchRetrievalConfidenceThresholdDefault,
  setDraftConversationalSearchConfidenceThresholdDefault,
  setDraftConversationalSearchVerbosityOptionDefault,
  setDraftConversationalSearchResponseConfidenceThresholdDefault,
  setDraftCitationsTitleValueDefault,
  setDraftMaxCitationsValueDefault,
  setDraftIsConversationalSearchEnabled,
  setDraftConversationalSearchRetrievalConfidenceThreshold,
  setDraftConversationalSearchConfidenceThreshold,
  setDraftConversationalSearchVerbosityOption,
  setDraftConversationalSearchResponseConfidenceThreshold,
  setDraftCitationsTitleValue,
  setDraftMaxCitationsValue,
  setDraftSuccessMessage,
  setDraftNoResultsMessage,
  setDraftConnectivityMessage,
  setDraftTitleOptionValue,
  setDraftBodyOptionValue,
  setDraftUrlOptionValue,
  updateDraftInstanceProjectIds,
  clearInstanceSettings,
  setDraftDiscoveryFilterValue,
  setDraftConversationalSearchMultiTurn,
  setDraftConversationalSearchMultiTurnDefault
} = slice.actions;

export default slice.reducer;
